import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const AnnouncementsNewsFeed = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [likedPosts, setLikedPosts] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [showComments, setShowComments] = useState({});
  const [comments, setComments] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareAnnouncementId, setShareAnnouncementId] = useState(null);

  // Fetch news/announcements from API (same source as News Page)
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/news/public?limit=50`);
      const data = await response.json();

      if (data.success) {
        // Map news data to announcements format
        const mappedAnnouncements = data.data.map(item => ({
          id: item.id,
          title: item.title,
          author: item.author || 'ARCA Admin',
          date: new Date(item.published_date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          content: item.excerpt,
          category: item.category,
          tags: [item.category],
          imageUrl: item.image_url,
          likes: item.like_count || 0,
          comments: item.comment_count || 0
        }));
        setAnnouncements(mappedAnnouncements);

        // Check liked status for each announcement
        checkLikedStatus(mappedAnnouncements);
      } else {
        toast.error('Failed to load announcements');
      }
    } catch (err) {
      console.error('Error fetching announcements:', err);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  // Check which posts the user has liked
  const checkLikedStatus = async (posts) => {
    const token = localStorage.getItem('authToken') ||
      localStorage.getItem('userToken') ||
      localStorage.getItem('directorToken');

    if (!token) return;

    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const likedStatus = {};

    for (const post of posts) {
      try {
        const response = await fetch(`${API_BASE}/news/${post.id}/like-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        likedStatus[post.id] = data.liked || false;
      } catch (err) {
        console.error(`Error checking like status for post ${post.id}:`, err);
      }
    }

    setLikedPosts(likedStatus);
  };

  // Toggle like on a post
  const handleLike = async (announcementId) => {
    const token = localStorage.getItem('authToken') ||
      localStorage.getItem('userToken') ||
      localStorage.getItem('directorToken');

    if (!token) {
      toast.error('Please log in to like posts');
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/news/${announcementId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        setLikedPosts(prev => ({ ...prev, [announcementId]: data.liked }));

        // Update like count
        setAnnouncements(prev => prev.map(announcement =>
          announcement.id === announcementId
            ? { ...announcement, likes: announcement.likes + (data.liked ? 1 : -1) }
            : announcement
        ));

        toast.success(data.liked ? '❤️ Liked!' : 'Like removed', {
          position: 'top-right',
          autoClose: 2000,
          style: { backgroundColor: '#6B8E23', color: 'white' }
        });
      }
    } catch (err) {
      console.error('Error toggling like:', err);
      toast.error('Failed to update like');
    }
  };

  // Load comments for a post
  const loadComments = async (announcementId) => {
    if (showComments[announcementId]) {
      // Hide comments
      setShowComments(prev => ({ ...prev, [announcementId]: false }));
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/news/${announcementId}/comments`);
      const data = await response.json();

      if (data.success) {
        setComments(prev => ({ ...prev, [announcementId]: data.data }));
        setShowComments(prev => ({ ...prev, [announcementId]: true }));
      }
    } catch (err) {
      console.error('Error loading comments:', err);
      toast.error('Failed to load comments');
    }
  };

  // Add comment to a post
  const handleAddComment = async (announcementId) => {
    const token = localStorage.getItem('authToken') ||
      localStorage.getItem('userToken') ||
      localStorage.getItem('directorToken');

    if (!token) {
      toast.error('Please log in to comment');
      return;
    }

    const content = commentInputs[announcementId];
    if (!content || content.trim().length === 0) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/news/${announcementId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });

      const data = await response.json();

      if (data.success) {
        // Update comment count
        setAnnouncements(prev => prev.map(announcement =>
          announcement.id === announcementId
            ? { ...announcement, comments: announcement.comments + 1 }
            : announcement
        ));

        // Add comment to local state
        setComments(prev => ({
          ...prev,
          [announcementId]: [data.data, ...(prev[announcementId] || [])]
        }));

        // Clear input
        setCommentInputs(prev => ({ ...prev, [announcementId]: '' }));

        // Show comments section
        setShowComments(prev => ({ ...prev, [announcementId]: true }));

        toast.success('💬 Comment added!', {
          position: 'top-right',
          autoClose: 2000,
          style: { backgroundColor: '#6B8E23', color: 'white' }
        });
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error('Failed to add comment');
    }
  };

  // Handle share button click
  const handleShare = (announcementId) => {
    setShareAnnouncementId(announcementId);
    setShowShareModal(true);
  };

  // Copy link to clipboard
  const copyShareLink = (announcementId) => {
    const shareUrl = `${window.location.origin}/community/announcements?id=${announcementId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success('🔗 Link copied to clipboard!', {
        position: 'top-right',
        autoClose: 2000,
        style: { backgroundColor: '#6B8E23', color: 'white' }
      });
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  // Share on social media
  const shareOnSocial = (platform, announcement) => {
    const shareUrl = `${window.location.origin}/community/announcements?id=${announcement.id}`;
    const shareText = `${announcement.title} - ${announcement.content.substring(0, 100)}...`;

    let url = '';

    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodeURIComponent(announcement.title)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
        break;
      default:
        return;
    }

    window.open(url, '_blank', 'width=600,height=400');
    setShowShareModal(false);
  };

  // Get unique categories for filter
  const categories = ['All', ...new Set(announcements.map(announcement => announcement.category))];

  // Filter announcements based on search and category
  const filteredAnnouncements = announcements.filter(announcement => {
    const matchesSearch = announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || announcement.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get category color
  const getCategoryColor = (category) => {
    const colorMap = {
      'Events': 'bg-primary',
      'Programs': 'bg-success',
      'Research': 'bg-info',
      'Training': 'bg-warning',
      'Partnerships': 'bg-danger',
      'Member Stories': 'bg-indigo',
      'Milestones': 'bg-purple',
      'Impact': 'bg-teal'
    };
    return colorMap[category] || 'bg-secondary';
  };

  return (
    <div className="container-fluid">
      {/* Search and Filter */}
      <div className="card shadow rounded p-4 mb-4">
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
          <h3 className="text-lg fw-medium mb-4 mb-md-0">Announcements & News</h3>
          <div className="d-flex flex-column flex-sm-row gap-2">
            <div className="position-relative">
              <input
                type="text"
                placeholder="Search announcements..."
                className="form-control ps-5"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg className="position-absolute" style={{ left: '15px', top: '10px' }} width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <select
              className="form-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card shadow rounded p-5 text-center">
          <div className="spinner-border mx-auto" style={{ color: '#6B8E23' }} role="status">
            <span className="visually-hidden">Loading announcements...</span>
          </div>
          <p className="mt-3 text-muted">Loading latest news and announcements...</p>
        </div>
      )}

      {/* Announcements */}
      {!loading && filteredAnnouncements.length > 0 ? (
        filteredAnnouncements.map(announcement => {
          const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
          const imageUrl = announcement.imageUrl && announcement.imageUrl.startsWith('/uploads/')
            ? `${API_BASE}${announcement.imageUrl}`
            : announcement.imageUrl;

          return (
            <div key={announcement.id} className="card shadow rounded p-4 mb-4">
              <div className="d-flex">
                <div className="bg-primary text-white rounded me-3 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"></path>
                  </svg>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                    <h3 className="text-lg fw-semibold">{announcement.title}</h3>
                    <span className={`badge ${getCategoryColor(announcement.category)} mt-2 mt-md-0`}>
                      {announcement.category}
                    </span>
                  </div>
                  <p className="text-sm text-muted mt-1">{announcement.date} • Posted by {announcement.author}</p>
                  <div className="mt-4">
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt={announcement.title}
                        className="rounded w-100"
                        style={{ maxHeight: '400px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {!imageUrl && (
                      <div className="bg-light border border-dashed rounded w-100" style={{ height: '192px' }}></div>
                    )}
                    <p className="mt-3">
                      {announcement.content}
                    </p>
                  </div>
                  <div className="mt-4">
                    {announcement.tags && announcement.tags.map((tag, index) => (
                      <span key={index} className="badge bg-secondary me-2 mb-2">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 d-flex align-items-center text-muted">
                    <button
                      className={`btn btn-sm ${likedPosts[announcement.id] ? 'btn-primary' : 'btn-outline-primary'} me-3`}
                      onClick={() => handleLike(announcement.id)}
                    >
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="me-1">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905a3.61 3.61 0 01-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
                      </svg>
                      {announcement.likes} {likedPosts[announcement.id] ? 'liked' : 'likes'}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary me-3"
                      onClick={() => loadComments(announcement.id)}
                    >
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="me-1">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                      </svg>
                      {announcement.comments} comments
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleShare(announcement.id)}
                    >
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="me-1">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
                      </svg>
                      Share
                    </button>
                  </div>

                  {/* Comment Input Section */}
                  <div className="mt-3">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add a comment..."
                        value={commentInputs[announcement.id] || ''}
                        onChange={(e) => setCommentInputs(prev => ({
                          ...prev,
                          [announcement.id]: e.target.value
                        }))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddComment(announcement.id);
                          }
                        }}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={() => handleAddComment(announcement.id)}
                        style={{ backgroundColor: '#6B8E23', border: 'none' }}
                      >
                        Post
                      </button>
                    </div>
                  </div>

                  {/* Comments Display */}
                  {showComments[announcement.id] && comments[announcement.id] && (
                    <div className="mt-3 border-top pt-3">
                      <h6 className="mb-3">Comments</h6>
                      {comments[announcement.id].length > 0 ? (
                        comments[announcement.id].map((comment, index) => {
                          const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
                          const avatarUrl = comment.profile_picture_url
                            ? `${API_BASE}${comment.profile_picture_url}`
                            : null;

                          return (
                            <div key={index} className="d-flex mb-3">
                              <div className="me-2">
                                {avatarUrl ? (
                                  <img
                                    src={avatarUrl}
                                    alt={`${comment.first_name} ${comment.last_name}`}
                                    className="rounded-circle"
                                    style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                    {comment.first_name?.charAt(0)}{comment.last_name?.charAt(0)}
                                  </div>
                                )}
                              </div>
                              <div className="flex-grow-1">
                                <div className="bg-light rounded p-2">
                                  <div className="fw-semibold small">
                                    {comment.first_name} {comment.last_name}
                                  </div>
                                  <div>{comment.content}</div>
                                </div>
                                <div className="text-muted small mt-1">
                                  {new Date(comment.created_at).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-muted">No comments yet. Be the first to comment!</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : !loading && (
        <div className="card shadow rounded p-5 text-center">
          <svg className="mx-auto" width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"></path>
          </svg>
          <h3 className="mt-3 text-lg fw-medium">No announcements found</h3>
          <p className="mt-2 text-muted">Try adjusting your search or filter criteria.</p>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (() => {
        const announcement = announcements.find(a => a.id === shareAnnouncementId);
        if (!announcement) return null;

        return (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowShareModal(false)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header" style={{ borderBottom: '2px solid #6B8E23' }}>
                  <h5 className="modal-title fw-bold">Share Announcement</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowShareModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <h6 className="fw-semibold">{announcement.title}</h6>
                    <p className="text-muted small">{announcement.content.substring(0, 100)}...</p>
                  </div>

                  {/* Copy Link Button */}
                  <button
                    className="btn btn-outline-primary w-100 mb-3"
                    onClick={() => copyShareLink(announcement.id)}
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="me-2">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                    Copy Link
                  </button>

                  <div className="text-center text-muted small mb-3">or share via</div>

                  {/* Social Media Buttons */}
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    <button
                      className="btn btn-primary"
                      style={{ backgroundColor: '#1877F2' }}
                      onClick={() => shareOnSocial('facebook', announcement)}
                      title="Share on Facebook"
                    >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </button>

                    <button
                      className="btn btn-info"
                      style={{ backgroundColor: '#1DA1F2' }}
                      onClick={() => shareOnSocial('twitter', announcement)}
                      title="Share on Twitter"
                    >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                      </svg>
                    </button>

                    <button
                      className="btn"
                      style={{ backgroundColor: '#0A66C2', color: 'white' }}
                      onClick={() => shareOnSocial('linkedin', announcement)}
                      title="Share on LinkedIn"
                    >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </button>

                    <button
                      className="btn btn-success"
                      style={{ backgroundColor: '#25D366' }}
                      onClick={() => shareOnSocial('whatsapp', announcement)}
                      title="Share on WhatsApp"
                    >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                    </button>

                    <button
                      className="btn btn-secondary"
                      onClick={() => shareOnSocial('email', announcement)}
                      title="Share via Email"
                    >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AnnouncementsNewsFeed;
